import React, { useState, useEffect } from 'react';
import { Typography, Container, Box, CircularProgress, Alert } from '@mui/material';
import { getAccountProjections } from '../services/api';

function BelieveItInPage() {
  const [projections, setProjections] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Format currency (exact legacy logic)
  const formatCurrency = (number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  };

  // Update values function
  const updateValues = async () => {
    try {
      const response = await getAccountProjections();
      setProjections(response.data);
      setError('');
      if (loading) setLoading(false);
    } catch (err) {
      console.error('Error fetching projections:', err);
      setError('Failed to load projections');
      if (loading) setLoading(false);
    }
  };

  useEffect(() => {
    // Update immediately on page load
    updateValues();
    
    // Set interval to update values every second (exact legacy behavior)
    const interval = setInterval(updateValues, 1000);
    
    // Cleanup interval when component unmounts
    return () => clearInterval(interval);
  }, []);

  if (loading || Object.keys(projections).length === 0) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container>
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          'Believe It In' Projections
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Bluebird section (exact legacy layout) */}
        {projections.Bluebird && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h1" gutterBottom>
              Bluebird Summary:
            </Typography>
            <Typography variant="h6" sx={{ ml: 6 }}>
              Tithe Total: {formatCurrency(projections.Bluebird['Tithe Total'] || 0)}
            </Typography>
            <Typography variant="h6" sx={{ ml: 6 }}>
              Personal Wealth Total: {formatCurrency(projections.Bluebird['Personal Wealth Total'] || 0)}
            </Typography>
            <Typography variant="h6" sx={{ ml: 6 }}>
              Fruits of Labor Total: {formatCurrency(projections.Bluebird['Fruits of Labor Total'] || 0)}
            </Typography>
            <Typography variant="h6" sx={{ ml: 6 }}>
              Misc_items Total: {formatCurrency(projections.Bluebird['Misc_items Total'] || 0)}
            </Typography>
            <Typography variant="h6" sx={{ ml: 6, mb: 2 }}>
              Uncategorized Cash Total: {formatCurrency(projections.Bluebird['Uncategorized Cash Total'] || 0)}
            </Typography>
            <Typography variant="h6" sx={{ ml: 6 }}>
              Sub-Accounts Total: {formatCurrency(projections.Bluebird['Bluebird Reserve Total'] || 0)}
            </Typography>
            <Typography variant="h6" sx={{ ml: 6, fontWeight: 'bold' }}>
              Full Total: {formatCurrency(projections.Bluebird['Bluebird Full Total'] || 0)}
            </Typography>
          </Box>
        )}

        {/* Chase_Lyft section */}
        {projections.Chase_Lyft && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h1" gutterBottom>
              Chase_Profits Summary:
            </Typography>
            <Typography variant="h6" sx={{ ml: 6 }}>
              Profits Taxes Total: {formatCurrency(projections.Chase_Lyft['Lyft Taxes Total'] || 0)}
            </Typography>
            <Typography variant="h6" sx={{ ml: 6 }}>
              Profits Car Total: {formatCurrency(projections.Chase_Lyft['Lyft Car Total'] || 0)}
            </Typography>
            <Typography variant="h6" sx={{ ml: 6 }}>
              Profits Insurance Total: {formatCurrency(projections.Chase_Lyft['Lyft Insurnace Total'] || 0)}
            </Typography>
            <Typography variant="h6" sx={{ ml: 6 }}>
              Profit Gains Total: {formatCurrency(projections.Chase_Lyft['Lyft Gains Total'] || 0)}
            </Typography>
            <Typography variant="h6" sx={{ ml: 6 }}>
              Misc Gains Total: {formatCurrency(projections.Chase_Lyft['Misc Gains Total'] || 0)}
            </Typography>
            <Typography variant="h6" sx={{ ml: 6, mb: 2 }}>
              Tithe Total: {formatCurrency(projections.Chase_Lyft['Tithe Total'] || 0)}
            </Typography>
            <Typography variant="h6" sx={{ ml: 6, fontWeight: 'bold' }}>
              Chase_Profits Total: {formatCurrency(projections.Chase_Lyft['Chase_Lyft Sum Total'] || 0)}
            </Typography>
          </Box>
        )}

        {/* Other accounts */}
        {['NSB', 'USBank', 'FirstBank'].map(accountName => {
          if (!projections[accountName]) {
            return null;
          }

          const accountData = projections[accountName];
          const sumTotalKey = `${accountName} Sum Total`;
          const sumTotalValue = accountData[sumTotalKey];

          // Filter out the sum total from other entries
          const otherEntries = Object.entries(accountData).filter(([key]) => key !== sumTotalKey);

          return (
            <Box key={accountName} sx={{ mb: 4 }}>
              <Typography variant="h5" component="h1" gutterBottom>
                {accountName} Summary:
              </Typography>
              {otherEntries.map(([key, value]) => (
                <Typography key={key} variant="h6" sx={{ ml: 6 }}>
                  {key.replace('_', ' ')}: {formatCurrency(value || 0)}
                </Typography>
              ))}
              {sumTotalValue !== undefined && (
                <Typography variant="h6" sx={{ ml: 6, mt: 2, fontWeight: 'bold' }}>
                  {sumTotalKey.replace('_', ' ')}: {formatCurrency(sumTotalValue || 0)}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Container>
  );
}

export default BelieveItInPage;